import { html } from 'htm/preact'
import { useState } from 'preact/hooks'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import { articlesApi } from '../services/api.js'

dayjs.extend(relativeTime)

function ScoreButton({ score, isSelected, onClick, isAI, disabled }) {
  const getScoreColor = (score) => {
    if (score >= 8) return 'bg-green-500 hover:bg-green-600 text-white'
    if (score >= 5) return 'bg-yellow-500 hover:bg-yellow-600 text-white'
    return 'bg-red-500 hover:bg-red-600 text-white'
  }

  const baseClasses = isSelected 
    ? getScoreColor(score) + ' ring-2 ring-offset-1 ring-gray-400'
    : 'bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300'

  const disabledClasses = disabled ? 'opacity-50 cursor-not-allowed' : ''

  return html`
    <button
      onClick=${onClick}
      class="w-7 h-7 rounded text-xs font-medium transition-all ${baseClasses} ${isAI || disabled ? 'cursor-default' : ''} ${disabledClasses}"
      title=${isAI ? 'AI Score' : disabled ? 'Autentificați-vă pentru a evalua' : `Set score to ${score}`}
      disabled=${isAI || disabled}
    >
      ${score}
    </button>
  `
}

function ScoreDisplay({ label, score, icon }) {
  if (score === null || score === undefined) return null

  const getScoreBadge = (score) => {
    if (score >= 8) return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
    if (score >= 5) return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
    return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
  }

  return html`
    <span class="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${getScoreBadge(score)}" title="${label}: ${score}">
      ${icon} ${score}
    </span>
  `
}

export function NewsListItem({ article, source, onScoreUpdate, isAuthenticated, onLoginClick }) {
  const [showScoring, setShowScoring] = useState(false)
  const [saving, setSaving] = useState(false)
  const [currentScore, setCurrentScore] = useState(article.human_score)
  const [error, setError] = useState(null)
  
  if (!article) return null

  const publishedAt = dayjs(article.published_at)
  const timeAgo = publishedAt.fromNow()
  const minutesAgo = dayjs().diff(publishedAt, 'minutes')
  const isNew = minutesAgo <= 5

  // Compute effective score for row highlighting (max of sonnet and haiku)
  const sonnet = article.sonnet_score
  const haiku = article.claude_score
  const highlightScore = Math.max(sonnet || 0, haiku || 0)

  const getRowHighlight = () => {
    if (highlightScore >= 9) return 'bg-red-100 dark:bg-red-900/30 border-l-4 border-l-red-500 animate-blink'
    if (highlightScore >= 8) return 'bg-red-50 dark:bg-red-900/20 border-l-4 border-l-red-400'
    if (highlightScore >= 7) return 'bg-yellow-200 dark:bg-yellow-800/30 border border-yellow-400 dark:border-yellow-600'
    if (highlightScore >= 6) return 'bg-yellow-100 dark:bg-yellow-800/20 border border-yellow-300 dark:border-yellow-600'
    if (highlightScore >= 5) return 'bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700'
    if (highlightScore >= 4) return 'bg-amber-50 dark:bg-yellow-900/10 border border-yellow-200 dark:border-yellow-700'
    return ''
  }
  const rowHighlight = getRowHighlight()
  
  const handleClick = (e) => {
    // Don't open article if clicking on scoring area
    if (e.target.closest('.scoring-area')) return
    window.open(article.url, '_blank', 'noopener,noreferrer')
  }

  const handleScoreClick = async (score, e) => {
    e.stopPropagation()
    if (saving || !isAuthenticated) return
    
    setSaving(true)
    setError(null)
    try {
      await articlesApi.updateScore(article.id, score)
      setCurrentScore(score)
      if (onScoreUpdate) onScoreUpdate(article.id, score)
    } catch (err) {
      console.error('Error updating score:', err)
      if (err.response?.status === 401) {
        setError('Sesiune expirată. Reconectați-vă.')
      } else {
        setError('Eroare la salvare')
      }
    } finally {
      setSaving(false)
    }
  }

  const toggleScoring = (e) => {
    e.stopPropagation()
    setShowScoring(!showScoring)
    setError(null)
  }

  const effectiveScore = currentScore ?? article.human_score

  return html`
    <article 
      class="border-b border-gray-200 dark:border-gray-700 py-4 px-4 hover:bg-gray-50
             dark:hover:bg-gray-800 transition-colors cursor-pointer
             ${rowHighlight ? rowHighlight : (isNew ? 'bg-green-50 dark:bg-green-900/20 border-l-4 border-l-green-500' : '')}"
      onClick=${handleClick}
    >
      <div class="flex items-start gap-4">
        ${isNew ? html`
          <div class="flex-shrink-0 mt-1">
            <span class="relative flex h-3 w-3">
              <span class="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
              <span class="relative inline-flex rounded-full h-3 w-3 bg-green-500"></span>
            </span>
          </div>
        ` : null}
        
        <div class="flex-grow min-w-0">
          <h3 class="text-lg font-semibold text-gray-900 dark:text-white hover:text-primary-600 
                     dark:hover:text-primary-400 transition-colors mb-2">
            ${article.title}
            ${isNew ? html`<span class="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800 dark:bg-green-800 dark:text-green-100">NOU</span>` : null}
          </h3>
          
          <div class="flex items-center flex-wrap gap-3 text-sm text-gray-500 dark:text-gray-400">
            <span class="font-medium">${source?.name || article.source_name || 'Unknown Source'}</span>
            <span>${timeAgo}</span>
            <span>${publishedAt.format('DD MMM YYYY, HH:mm')}</span>
            
            ${isAuthenticated ? html`
              <!-- Score badges (authenticated only) -->
              <div class="flex items-center gap-2">
                ${article.claude_score != null && article.claude_score === 0
                  ? html`<span class="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-gray-200 text-gray-500 dark:bg-gray-700 dark:text-gray-400 line-through" title="Clickbait (Haiku: 0)">🟣 Clickbait</span>`
                  : article.claude_score ? html`<${ScoreDisplay} label="Haiku" score=${article.claude_score} icon="🟣" />` : null}
                ${article.sonnet_score != null && article.sonnet_score === 0
                  ? html`<span class="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-gray-200 text-gray-500 dark:bg-gray-700 dark:text-gray-400 line-through" title="Clickbait (Sonnet: 0)">🔵 Clickbait</span>`
                  : article.sonnet_score ? html`<${ScoreDisplay} label="Sonnet" score=${article.sonnet_score} icon="🔵" />` : null}
                ${effectiveScore ? html`<${ScoreDisplay} label="Human" score=${effectiveScore} icon="👤" />` : null}
              </div>

              <!-- Score toggle button -->
              <button
                onClick=${toggleScoring}
                class="scoring-area ml-2 px-2 py-1 text-xs rounded transition-colors
                       ${showScoring
                         ? 'bg-primary-100 text-primary-700 dark:bg-primary-900 dark:text-primary-300'
                         : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'}"
              >
                ${showScoring ? '✕ Close' : '📊 Score'}
              </button>
            ` : null}
          </div>
          
          <!-- Scoring panel -->
          ${showScoring ? html`
            <div class="scoring-area mt-3 p-3 bg-gray-100 dark:bg-gray-800 rounded-lg" onClick=${(e) => e.stopPropagation()}>
              ${isAuthenticated ? html`
                <div class="flex items-center gap-2 flex-wrap">
                  <span class="text-xs text-gray-500 dark:text-gray-400 mr-2">Rate relevance:</span>
                  ${[1,2,3,4,5,6,7,8,9,10].map(score => html`
                    <${ScoreButton} 
                      score=${score} 
                      isSelected=${effectiveScore === score}
                      onClick=${(e) => handleScoreClick(score, e)}
                      disabled=${saving}
                    />
                  `)}
                  ${saving ? html`<span class="ml-2 text-xs text-gray-500">Saving...</span>` : null}
                  ${error ? html`<span class="ml-2 text-xs text-red-500">${error}</span>` : null}
                </div>
                <div class="mt-2 text-xs text-gray-500 dark:text-gray-400">
                  <span class="text-red-500">1-4:</span> Low relevance | 
                  <span class="text-yellow-500">5-7:</span> Medium | 
                  <span class="text-green-500">8-10:</span> High relevance for Romanian audience
                </div>
              ` : html`
                <div class="flex items-center gap-3">
                  <span class="text-sm text-gray-600 dark:text-gray-400">
                    🔒 Autentificați-vă pentru a evalua articolele
                  </span>
                  <button
                    onClick=${(e) => { e.stopPropagation(); onLoginClick && onLoginClick(); }}
                    class="px-3 py-1.5 text-sm font-medium text-white bg-primary-600 rounded-lg 
                           hover:bg-primary-700 transition-colors"
                  >
                    Login
                  </button>
                </div>
              `}
            </div>
          ` : null}
        </div>
        
        <div class="flex-shrink-0 self-center">
          <span class="text-gray-400">→</span>
        </div>
      </div>
    </article>
  `
}
